import React from 'react';
import { CheckCircle, Clock, FileText } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Assessment } from '../types';

const AVAILABLE_ASSESSMENTS = ['pa', 'pia', 'dpia'];

interface AssessmentCardProps {
  assessment: Assessment;
}

export default function AssessmentCard({ assessment }: AssessmentCardProps) {
  const navigate = useNavigate();
  // Check if the assessment should show "for one region"
  const showRegionText = AVAILABLE_ASSESSMENTS.includes(assessment.id);
  const isAvailable = AVAILABLE_ASSESSMENTS.includes(assessment.id);

  const handleStartClick = () => {
    navigate(`/checkout/${assessment.id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100 hover:border-brand-primary transition-colors duration-300">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary/5 to-brand-primary/10 p-6 border-b border-gray-100">
        <h3 className="text-2xl font-bold text-gray-700 mb-2 text-center font-montserrat">{assessment.name}</h3>
        <div className="flex flex-col items-center">
          <div className="flex items-baseline justify-center space-x-2">
            <span className="text-sm text-gray-500">Starting at</span>
            <span className="text-3xl font-bold text-brand-primary">${assessment.price}</span>
            <span className="text-sm text-gray-500">USD</span>
          </div>
          {showRegionText && (
            <span className="text-sm text-gray-500 mt-1">+$150 per additional region</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-6">
        {/* Description */}
        <p className="text-gray-600 text-center mb-6">{assessment.description}</p>

        {/* Timeline */}
        <div className="flex items-center justify-center text-gray-500 mb-6">
          <Clock className="h-5 w-5 mr-2" />
          <span>{assessment.estimatedTime}</span>
        </div>

        {/* Features */}
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 mb-4 flex items-center justify-center">
            <FileText className="h-5 w-5 mr-2 text-brand-primary" />
            What's Included
          </h4>
          <ul className="space-y-3">
            {assessment.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle className="h-5 w-5 text-brand-primary mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        {isAvailable ? (
          <div className="space-y-3 pt-6 mt-auto">
            <button
              onClick={handleStartClick}
              className="block w-full bg-brand-primary text-white text-center py-3 rounded-md hover:bg-brand-dark transition-colors"
            >
              START
            </button>
            <Link
              to="/learn-more"
              className="block w-full border border-brand-primary text-brand-primary text-center py-3 rounded-md hover:bg-emerald-50 transition-colors"
            >
              Learn More
            </Link>
          </div>
        ) : (
          <div className="pt-6 mt-auto">
            <Link
              to="/learn-more"
              className="block w-full border border-brand-primary text-brand-primary text-center py-3 rounded-md hover:bg-emerald-50 transition-colors"
            >
              Learn More
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}